import React, { useState, useEffect } from 'react';

/**
 * CaseReference - Clickable citation component
 *
 * Transforms case IDs like "miranda-v-arizona-1966" into clickable links
 * that navigate to the Reference Library and highlight the cited case.
 */

/**
 * Format case ID to human-readable name
 * Example: "miranda-v-arizona-1966" → "Miranda v. Arizona (1966)"
 */
export function formatCaseName(caseId) {
  if (!caseId) return 'Unknown Case';

  const parts = caseId.split('-');

  // Extract year if last part is a 4-digit number
  let year = '';
  if (parts.length > 0 && /^\d{4}$/.test(parts[parts.length - 1])) {
    year = parts.pop();
  }

  // Convert remaining parts to title case, handling 'v' specially
  const name = parts
    .map((part, index) => {
      // 'v' becomes 'v.' for legal citation style
      if (part.toLowerCase() === 'v') {
        return 'v.';
      }
      // Title case for other words
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ');

  return year ? `${name} (${year})` : name;
}

/**
 * Parse text content and extract case citations
 * Returns array of {type: 'text'|'citation', content: string, caseId?: string}
 */
export function parseCitations(text) {
  if (!text || typeof text !== 'string') return [{ type: 'text', content: text || '' }];

  const casePattern = /\[CASE:([a-zA-Z0-9-]+)\]/g;
  const segments = [];
  let lastIndex = 0;
  let match;

  while ((match = casePattern.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: text.slice(lastIndex, match.index)
      });
    }

    // Add the citation
    segments.push({
      type: 'citation',
      content: match[0],
      caseId: match[1]
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.slice(lastIndex)
    });
  }

  return segments.length > 0 ? segments : [{ type: 'text', content: text }];
}

/**
 * CaseReference Component
 * A clickable citation that navigates to Reference Library
 */
function CaseReference({ caseId, onCaseClick }) {
  const [isHovering, setIsHovering] = useState(false);
  const displayName = formatCaseName(caseId);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onCaseClick) {
      onCaseClick(caseId);
    }
  };

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      className="case-citation-link"
      title={`View ${displayName} in Reference Library`}
      style={{
        color: isHovering ? '#a5b4fc' : '#818cf8',
        fontStyle: 'italic',
        textDecoration: 'underline',
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit',
        display: 'inline'
      }}
    >
      {displayName}
    </button>
  );
}

/**
 * Process React children and replace citation patterns with CaseReference components
 * Handles both string children and nested React elements
 */
export function processChildrenWithCitations(children, onCaseClick) {
  if (!children) return children;

  // Handle array of children
  if (Array.isArray(children)) {
    return children.map((child, index) =>
      processChildrenWithCitations(child, onCaseClick)
    );
  }

  // Handle string content
  if (typeof children === 'string') {
    const segments = parseCitations(children);

    // If no citations found, return original string
    if (segments.length === 1 && segments[0].type === 'text') {
      return children;
    }

    // Return mixed content with CaseReference components
    return segments.map((segment, index) => {
      if (segment.type === 'citation') {
        return (
          <CaseReference
            key={`citation-${index}-${segment.caseId}`}
            caseId={segment.caseId}
            onCaseClick={onCaseClick}
          />
        );
      }
      return segment.content;
    });
  }

  // Handle React elements - process their children recursively
  if (React.isValidElement(children)) {
    const childProps = children.props;

    if (childProps && childProps.children) {
      const processedChildren = processChildrenWithCitations(
        childProps.children,
        onCaseClick
      );

      // Only clone if children actually changed
      if (processedChildren !== childProps.children) {
        return React.cloneElement(children, {}, processedChildren);
      }
    }
  }

  return children;
}

export default CaseReference;
